import {
  Badge,
  GridSection,
  GridSectionBody,
  GridSectionHeader,
  GridSectionItem,
  GridSectionSpan,
  Timeline,
} from "@components";
import CardNetworkIcon from "@components/visual/CardNetworkIcon";
import { formatAmount, formatDateTime } from "@utils";
import { CheckLg, XLg } from "react-bootstrap-icons";
import { ReceiptRefundIcon } from "@heroicons/react/24/outline";
import { transactionStatusColors, refundStatusLabel } from "../../../common";
import { reconciledTimelineEntry } from "./reconciledTimelineEntry";

// Lifecycle feed for a refund that has no payment: issued on the terminal, then its terminal
// state. The Timeline sorts by date.
const mapTimeline = (refund, reconciledLabel, reconciledByName) => {
  const events = [
    {
      title: "Refund issued on terminal",
      subTitle: formatDateTime(refund.issuesOn),
      date: refund.issuesOn,
      nodeContent: { icon: ReceiptRefundIcon },
    },
  ];

  const label = refundStatusLabel(refund.status);

  if (label === "Refunded" && refund.settledOn) {
    events.push({
      title: "Refunded",
      subTitle: formatDateTime(refund.settledOn),
      amount: formatAmount(refund.amount),
      date: refund.settledOn,
      nodeContent: { icon: CheckLg, color: "success" },
    });
  } else if (label === "Failed") {
    events.push({
      title: "Refund failed",
      subTitle: refund.settledOn ? formatDateTime(refund.settledOn) : undefined,
      date: refund.settledOn ?? refund.issuesOn,
      nodeContent: { icon: XLg, color: "red" },
    });
  }

  return [...events, ...reconciledTimelineEntry(reconciledLabel, reconciledByName)];
};

// A refund issued on a POS terminal to any card, with no payment behind it (Adyen's
// "unreferenced refund"). There is no customer and no linked payment to show.
const UnreferencedRefundDetails = ({ refund, reconciledLabel, reconciledByName }) => {
  const statusLabel = refundStatusLabel(refund.status);
  const hasCard = refund.cardNetwork || refund.last4Digits;

  return (
    <>
      <GridSection>
        <GridSectionSpan>
          <h2 className="text-3xl font-medium m-0 mr-2">
            {formatAmount(refund.amount)}
          </h2>
          <Badge color={transactionStatusColors[statusLabel]}>{statusLabel}</Badge>
        </GridSectionSpan>
        <GridSectionItem>
          <GridSectionHeader>Source</GridSectionHeader>
          <GridSectionBody>{refund.source}</GridSectionBody>
        </GridSectionItem>
      </GridSection>

      <div className="w-full flex items-center px-5 pt-5">
        <p className="font-semibold m-0">Refund details</p>
      </div>
      <GridSection className="grid-cols-2">
        <GridSectionItem>
          <GridSectionHeader>Refund created on</GridSectionHeader>
          <GridSectionBody>{formatDateTime(refund.issuesOn)}</GridSectionBody>
        </GridSectionItem>
        {refund.settledOn && (
          <GridSectionItem>
            <GridSectionHeader>Refunded on</GridSectionHeader>
            <GridSectionBody>{formatDateTime(refund.settledOn)}</GridSectionBody>
          </GridSectionItem>
        )}
        {refund.terminalName && (
          <GridSectionItem>
            <GridSectionHeader>Terminal name</GridSectionHeader>
            <GridSectionBody>{refund.terminalName}</GridSectionBody>
          </GridSectionItem>
        )}
        {refund.terminalTransactionId && (
          <GridSectionItem>
            <GridSectionHeader>Terminal transaction ID</GridSectionHeader>
            <GridSectionBody>{refund.terminalTransactionId}</GridSectionBody>
          </GridSectionItem>
        )}
        <GridSectionSpan>
          <p className="text-xs text-zinc-500 m-0">
            Issued directly on the terminal without selecting an original payment.
          </p>
        </GridSectionSpan>
      </GridSection>

      {hasCard && (
        <>
          <div className="w-full flex items-center px-5 pt-5">
            <p className="font-semibold m-0">Card details</p>
          </div>
          <GridSection>
            <GridSectionItem>
              <GridSectionHeader>Card</GridSectionHeader>
              <GridSectionBody>
                <span className="flex items-center gap-2">
                  {refund.cardNetwork && (
                    <CardNetworkIcon network={refund.cardNetwork} />
                  )}
                  {refund.last4Digits && (
                    <span>
                      &bull;&bull;&bull;&bull; {refund.last4Digits}
                    </span>
                  )}
                </span>
              </GridSectionBody>
            </GridSectionItem>
          </GridSection>
        </>
      )}

      <div className="border-t border-zinc-200">
        <Timeline activity={mapTimeline(refund, reconciledLabel, reconciledByName)} />
      </div>
    </>
  );
};

export default UnreferencedRefundDetails;
